import { useEffect, useState } from "react";
import { FaServer, FaPlus, FaArchive, FaPen, FaStar, FaRegStar } from "react-icons/fa";
import { ENVIRONMENTS_CONSTANTS } from "../_MODULES/_API";
import { OPTIONS_ENVIRONMENT_CODE, onFindOptionLabel } from "../../../_Catalogs.Views";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { Card, EmptyState, Skeleton, Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { FormGrid, FieldInput, FieldSelect, FieldTextarea, FieldSwitch, useForm } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { onApiAction } from "../../../../_UI/onApiAction";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { onSilentPatch } from "../../../../_UI/onSilentPatch";
import { useDateRange, onMatchesRange } from "../../../../_UI/useDateRange";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { usePagedList } from "../../../../_UI/usePagedList";

const EMPTY_VALUES = { name: "", code: "LOCAL", description: "", baseUrl: "", apiUrl: "", branch: "", isFavorite: false };

export const PanelEnvironments = ({ subProjectId }) => {

    const [useSearch, setSearch] = useState("");

    const DATE_RANGE = useDateRange();
    const [useIsModalOpen, setIsModalOpen] = useState(false);
    const [useEnvironmentEdit, setEnvironmentEdit] = useState(null);

    const { apiGet: onGetEnvironments, useApiData: ENVIRONMENTS, useApiLoading: IS_LOADING, mutateLocal } = useApiQuery({
        minTimeQuery: 250, enablePagination: false, startLoading: true,
    });

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 300,
    });

    const onLoadEnvironments = () => onGetEnvironments({
        endpoint: ENVIRONMENTS_CONSTANTS.BY_SUBPROJECT, isDetail: true, config: { pathParams: { subProjectId } },
    });

    useEffect(() => { onLoadEnvironments(); }, [subProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

    const FORM = useForm({
        initialValues: EMPTY_VALUES,

        schema: { name: { required: true } },

        onSubmit: async (values) => {

            const IS_EDIT = !!useEnvironmentEdit?.id;

            const PAYLOAD = {
                name: values.name.trim(), code: values.code,
                description: values.description || "",
                baseUrl: values.baseUrl || "",
                apiUrl: values.apiUrl || "",
                branch: values.branch || "",
                isFavorite: !!values.isFavorite,
            };

            if (!IS_EDIT) PAYLOAD.subProjectId = subProjectId;

            const RESPONSE = IS_EDIT
                ? await actionMutate({ method: "PATCH", endpoint: ENVIRONMENTS_CONSTANTS.UPDATE, successMessage: "Entorno actualizado", id: useEnvironmentEdit.id, data: PAYLOAD })
                : await actionMutate({ method: "POST", endpoint: ENVIRONMENTS_CONSTANTS.CREATE, successMessage: "Entorno creado", data: PAYLOAD });

            if (!RESPONSE.success) { FORM.applyApiError(RESPONSE); return; }

            mutateLocal(IS_EDIT
                ? { id: useEnvironmentEdit.id, patch: RESPONSE.data }
                : { insert: RESPONSE.data });

            FORM.reset();
            setEnvironmentEdit(null);
            setIsModalOpen(false);
        },
    });

    const onOpenCreate = () => {
        setEnvironmentEdit(null);
        FORM.setValues(EMPTY_VALUES);
        setIsModalOpen(true);
    };

    const onOpenEdit = (environment) => {
        setEnvironmentEdit(environment);
        FORM.setValues({
            name: environment.name ?? "",
            code: environment.code ?? "LOCAL",
            description: environment.description ?? "",
            baseUrl: environment.baseUrl ?? "",
            apiUrl: environment.apiUrl ?? "",
            branch: environment.branch ?? "",
            isFavorite: !!environment.isFavorite,
        });
        setIsModalOpen(true);
    };

    const onCloseModal = () => {
        setIsModalOpen(false);
        setEnvironmentEdit(null);
    };

    const onDelete = async (environment) => {

        const IS_CONFIRMED = await confirm.danger("Archivar entorno",
            `Se archiva "${environment.name}". Las credenciales asociadas quedan sin entorno.`);

        if (!IS_CONFIRMED) return;

        const RESPONSE = await onApiAction({
            method: "PATCH", endpoint: ENVIRONMENTS_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: environment.id }, successMessage: "Entorno archivado",
        });

        if (RESPONSE.success) mutateLocal({ id: environment.id, remove: true });
    };


    const onToggleFavorite = async (environment) => {
        const previous = !!environment.isFavorite;
        const next = !previous;
        mutateLocal({ id: environment.id, patch: { isFavorite: next } });

        const RESPONSE = await onSilentPatch({
            endpoint: ENVIRONMENTS_CONSTANTS.UPDATE,
            id: environment.id,
            data: { isFavorite: next },
            errorMessage: "No se pudo guardar el destacado del entorno.",
        });

        if (!RESPONSE.success) mutateLocal({ id: environment.id, patch: { isFavorite: previous } });
    };

    const ITEMS = [...(ENVIRONMENTS ?? [])].sort((a, b) => (Number(!!b.isFavorite) - Number(!!a.isFavorite)) || (Number(a.position ?? 0) - Number(b.position ?? 0)) || String(a.name ?? "").localeCompare(String(b.name ?? "")));

    const VISIBLE = ITEMS.filter((environment) => onMatchesRange(environment, DATE_RANGE.useRange, "createdAt"))
        .filter((environment) => onMatchesSearch(useSearch, [
        environment.name, environment.code, environment.description, environment.baseUrl, environment.apiUrl, environment.branch,
    ]));

    const PAGER = usePagedList(VISIBLE, 6, "environmentsPage");

    return (
        <>
            <div className="LStudio__app_panel_bar">


                <ViewToolbar
                    filters={
                        <SearchFilter value={useSearch} onChange={setSearch} total={ITEMS.length} matches={VISIBLE.length}
                            placeholder="Ej.: Producción, main o api.midominio.com..." />
                    }
                    dateRange={DATE_RANGE}
                    total={PAGER.total} perPage={PAGER.usePerPage} onChangePerPage={PAGER.onChangePerPage}
                    exportColumns={[
                        { key: "name", label: "Entorno" },
                        { key: "code", label: "Tipo" },
                        { key: "baseUrl", label: "Frontend" },
                        { key: "apiUrl", label: "API" },
                        { key: "branch", label: "Rama" },
                    ]}
                    exportRows={VISIBLE.map((environment) => ({
                        name: environment.name,
                        code: onFindOptionLabel(OPTIONS_ENVIRONMENT_CODE, environment.code),
                        baseUrl: environment.baseUrl ?? "",
                        apiUrl: environment.apiUrl ?? "",
                        branch: environment.branch ?? "",
                    }))}
                    exportName="entornos"
                    actions={<AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nuevo entorno</AppButton>} />

            </div>

            {IS_LOADING && <Skeleton variant="box" height={130} />}

            {
                !IS_LOADING && VISIBLE.length === 0 && (
                    <EmptyState icon={<FaServer />} title="Sin entornos definidos"
                        description="Local, desarrollo, staging y producción. Las credenciales cuelgan de acá." />
                )
            }

            {
                !IS_LOADING && VISIBLE.length > 0 && (
                    <div className="LStudio__app_environments_grid">
                        {
                            PAGER.paged.map((environment) => (
                                <Card key={environment.id} className="LStudio__app_environments_item">

                                    <div className="LStudio__app_environments_item_head">

                                        <span className="LStudio__app_environments_item_name">
                                            {environment.isFavorite && <FaStar className="LStudio__app_record_star" title="Destacado" />}
                                            {environment.name}
                                        </span>

                                        <Badge tone="accent" size="sm">
                                            {onFindOptionLabel(OPTIONS_ENVIRONMENT_CODE, environment.code)}
                                        </Badge>

                                    </div>

                                    {environment.description && <p className="LStudio__app_environments_item_desc">{environment.description}</p>}

                                    <dl className="LStudio__app_environments_item_meta">
                                        {environment.baseUrl && <><dt>Frontend</dt><dd>{environment.baseUrl}</dd></>}
                                        {environment.apiUrl && <><dt>API</dt><dd>{environment.apiUrl}</dd></>}
                                        {environment.branch && <><dt>Rama</dt><dd>{environment.branch}</dd></>}
                                    </dl>

                                    <div className="LStudio__app_environments_item_actions">

                                        <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly
                                            icon={environment.isFavorite ? <FaStar /> : <FaRegStar />}
                                            aria-label={environment.isFavorite ? "Quitar de destacados" : "Destacar entorno"}
                                            title={environment.isFavorite ? "Quitar de destacados" : "Destacar entorno"}
                                            onClick={() => onToggleFavorite(environment)} />

                                        <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaPen />}
                                            onClick={() => onOpenEdit(environment)}>Editar</AppButton>

                                        <AppButton size="sm" variant="ghost" tone="danger" icon={<FaArchive />}
                                            onClick={() => onDelete(environment)}>Archivar</AppButton>

                                    </div>

                                </Card>
                            ))
                        }
                    </div>
                )
            }

            <ViewPagination page={PAGER.usePage} totalPages={PAGER.totalPages}
                total={PAGER.total} perPage={PAGER.usePerPage} onChange={PAGER.onChangePage} />

            <AppModal isOpen={useIsModalOpen} onClose={onCloseModal}
                isDirty={FORM.isDirty} size="md"
                title={useEnvironmentEdit ? "Editar entorno" : "Nuevo entorno"}
                footer={
                    <>
                        <AppButton variant="ghost" tone="neutral" onClick={onCloseModal}>Cancelar</AppButton>
                        <AppButton onClick={FORM.onSubmit} isLoading={useActionApiIsLoading}>
                            {useEnvironmentEdit ? "Guardar cambios" : "Guardar"}
                        </AppButton>
                    </>
                } >

            <div className="LStudio__app_priority_toggle">
                <FieldSwitch id="environment_isFavorite" label="Destacado" text="Mostrar este entorno primero"
                    isChecked={!!FORM.values.isFavorite}
                    onChange={(event) => FORM.setValue("isFavorite", event.target.checked)} />
            </div>

                <FormGrid columns={2}>

                    <FieldInput {...FORM.field("name")} label="Nombre" placeholder="Ej: Produccion" />

                    <FieldSelect {...FORM.field("code")} label="Tipo" options={OPTIONS_ENVIRONMENT_CODE} isSearchable />

                    <FieldTextarea {...FORM.field("description")} label="Descripción" rows={2} span={2} placeholder="Ej: Portal interno de operaciones" />

                    <FieldInput {...FORM.field("baseUrl")} label="URL del frontend" placeholder="Ej: https://app.example.com" />

                    <FieldInput {...FORM.field("apiUrl")} label="URL de la API" placeholder="Ej: https://app.example.com" />

                    <FieldInput {...FORM.field("branch")} label="Rama de git" placeholder="Ej: main" />


                </FormGrid>

            </AppModal>
        </>
    );
};
